import React, { useState } from 'react';
import { Input } from './ui/input';

/**
 * Componente de input para RUT chileno con formateo automático
 * Formato: XX.XXX.XXX-X
 */
const RutInput = React.forwardRef(({ value, onChange, onValidChange, ...props }, ref) => {
  const [error, setError] = useState('');

  // Limpiar RUT: remover todo excepto números y K
  const cleanRut = (rut) => {
    return rut.replace(/[^0-9kK]/g, '').toUpperCase();
  };

  // Formatear RUT: agregar puntos y guión
  const formatRut = (rut) => {
    const cleaned = cleanRut(rut);
    if (cleaned.length === 0) return '';
    
    // Separar número y dígito verificador
    const dv = cleaned.slice(-1);
    const numero = cleaned.slice(0, -1);
    
    if (numero.length === 0) return dv;
    
    // Agregar puntos cada 3 dígitos desde la derecha
    let formatted = '';
    for (let i = numero.length - 1, count = 0; i >= 0; i--, count++) {
      if (count > 0 && count % 3 === 0) {
        formatted = '.' + formatted;
      }
      formatted = numero[i] + formatted;
    }
    
    return `${formatted}-${dv}`;
  };

  // Validar RUT chileno
  const validateRut = (rut) => {
    const cleaned = cleanRut(rut);
    
    if (cleaned.length < 2) {
      return { valid: false, error: 'RUT muy corto' };
    }
    
    const numero = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);
    
    if (!/^\d+$/.test(numero)) {
      return { valid: false, error: 'RUT inválido' };
    }
    
    // Calcular dígito verificador
    let suma = 0;
    let multiplicador = 2;
    
    for (let i = numero.length - 1; i >= 0; i--) {
      suma += parseInt(numero[i]) * multiplicador;
      multiplicador = multiplicador < 7 ? multiplicador + 1 : 2;
    }
    
    const resto = suma % 11;
    const dvCalculado = 11 - resto;
    
    let dvEsperado;
    if (dvCalculado === 11) {
      dvEsperado = '0';
    } else if (dvCalculado === 10) {
      dvEsperado = 'K';
    } else {
      dvEsperado = String(dvCalculado);
    }
    
    if (dv !== dvEsperado) {
      return { valid: false, error: 'Dígito verificador inválido' };
    }
    
    return { valid: true, error: '' };
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    const cleaned = cleanRut(inputValue);
    const formatted = formatRut(cleaned);
    
    // Validar
    let validation = { valid: false, error: '' };
    if (cleaned.length >= 2) {
      validation = validateRut(cleaned);
      setError(validation.error);
    } else {
      setError('');
    }
    
    // Notificar cambio con el valor formateado
    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: formatted
        }
      };
      onChange(syntheticEvent);
    }
    
    // Notificar validación
    if (onValidChange) {
      onValidChange(validation.valid, cleaned);
    }
  };

  return (
    <div className="space-y-1">
      <Input
        ref={ref}
        {...props}
        value={value}
        onChange={handleChange}
        placeholder="12.345.678-9"
        className={error ? 'border-red-500' : props.className}
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
});

RutInput.displayName = 'RutInput';

export default RutInput;
